import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import PlaceIcon from '@mui/icons-material/Place';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import MapIcon from '@mui/icons-material/Map';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import pathfinderApi, { OverwatchArticle } from '../../services/pathfinderApi';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';

type SearchMode = 'keyword' | 'area' | 'radius';
type LanguageFilter = 'all' | 'en' | 'foreign';

interface OverwatchPanelProps {
  overwatchEnabled: boolean;
  dateRange: { start: Date | null; end: Date | null };
  queryTimeZone?: string;
  /** Current map center [lat, lng] — used for radius search. */
  mapCenter?: [number, number] | null;
  /** Current map extent — used for area (bbox) search. */
  mapBounds?: { north: number; south: number; east: number; west: number } | null;
  loadedArticles?: OverwatchArticle[];
  onLoadArticlesToMap?: (articles: OverwatchArticle[]) => void;
  onClearArticles?: () => void;
  onZoomTo?: (lat: number, lng: number) => void;
  /** Fit the map to the bounding box of all provided locations. */
  onZoomToExtent?: (locations: Array<{ latitude: number; longitude: number }>) => void;
  /** Persist an article to the Intel Packet. Returns true on success. */
  onSaveToIntel?: (article: OverwatchArticle) => Promise<boolean>;
}

const SENTIMENT_COLOR: Record<string, string> = {
  positive: '#4caf50',
  neutral: '#9e9e9e',
  negative: '#f44336',
};

const formatDate = (iso?: string): string => {
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const OverwatchPanel: React.FC<OverwatchPanelProps> = ({
  overwatchEnabled,
  dateRange,
  queryTimeZone,
  mapCenter,
  mapBounds,
  loadedArticles = [],
  onLoadArticlesToMap,
  onClearArticles,
  onZoomTo,
  onZoomToExtent,
  onSaveToIntel,
}) => {
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const [promoteArticle, setPromoteArticle] = useState<OverwatchArticle | null>(null);
  const [mode, setMode] = useState<SearchMode>('keyword');
  const [language, setLanguage] = useState<LanguageFilter>('all');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [days, setDays] = useState(30);
  const [radiusKm, setRadiusKm] = useState(50);

  const [results, setResults] = useState<OverwatchArticle[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<OverwatchArticle | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  // Open the detail dialog immediately with the list row, then fetch the fully
  // enriched article (full body, all locations, uncapped entities) and merge.
  const openDetail = useCallback(async (article: OverwatchArticle) => {
    setDetail(article);
    setDetailLoading(true);
    try {
      const full = await pathfinderApi.getOverwatchArticle(article.id);
      setDetail((cur) => (cur && cur.id === article.id ? { ...cur, ...full } : cur));
    } catch {
      // Keep the list row on failure; enrichment is best-effort.
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Load category choices once the module is confirmed available.
  useEffect(() => {
    if (!overwatchEnabled) return;
    let cancelled = false;
    pathfinderApi
      .getOverwatchCategories()
      .then((cats) => { if (!cancelled) setCategories(cats); })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, [overwatchEnabled]);

  const hasExplicitRange = !!(dateRange.start && dateRange.end);

  const handleSearch = useCallback(async () => {
    setSearching(true);
    setError(null);
    try {
      const params: Parameters<typeof pathfinderApi.searchOverwatch>[0] = {
        mode,
        query: query.trim() || undefined,
        language,
        category: category || undefined,
        days,
        limit: 200,
        timeZone: queryTimeZone,
      };
      if (hasExplicitRange) {
        params.dateRange = [dateRange.start as Date, dateRange.end as Date];
      }
      if (mode === 'area') {
        if (!mapBounds) {
          setError('Pan/zoom the map to the area you want to search, then try again.');
          setSearching(false);
          return;
        }
        params.minLat = mapBounds.south;
        params.maxLat = mapBounds.north;
        params.minLon = mapBounds.west;
        params.maxLon = mapBounds.east;
      } else if (mode === 'radius') {
        if (!mapCenter) {
          setError('No map center available for radius search.');
          setSearching(false);
          return;
        }
        params.centerLat = mapCenter[0];
        params.centerLon = mapCenter[1];
        params.radiusKm = radiusKm;
      }
      const res = await pathfinderApi.searchOverwatch(params);
      setResults(res.articles || []);
      if ((res.articles || []).length === 0) {
        setError('No media matched these filters.');
      }
    } catch (e: any) {
      setError(e?.message || 'Search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [mode, query, language, category, days, hasExplicitRange, dateRange, mapBounds, mapCenter, radiusKm, queryTimeZone]);

  const geolocated = useMemo(
    () => results.filter((a) => (a.locations?.length || 0) > 0 || (a.latitude != null && a.longitude != null)),
    [results],
  );
  const totalPoints = useMemo(
    () => geolocated.reduce((sum, a) => sum + Math.max(1, a.locations?.length || 0), 0),
    [geolocated],
  );

  const handleSave = useCallback(async (article: OverwatchArticle) => {
    if (intelEnabled) {
      setPromoteArticle(article);
      return;
    }
    if (!onSaveToIntel) return;
    const ok = await onSaveToIntel(article);
    if (ok) {
      setSavedIds((prev) => new Set(prev).add(article.id));
    }
  }, [onSaveToIntel, intelEnabled]);

  if (!overwatchEnabled) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <NewspaperIcon sx={{ fontSize: 18, color: '#ff9800' }} /> OSINT
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
          OSINT media search is not available. Enable it in Settings → Modules and make sure the
          DataHog (sandbox) database is configured on the server.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <NewspaperIcon sx={{ fontSize: 18, color: '#ff9800' }} /> OSINT
      </Typography>

      {/* Search mode */}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, v) => { if (v) setMode(v); }}
        sx={{ mb: 1 }}
        fullWidth
      >
        <ToggleButton value="keyword" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Keyword</ToggleButton>
        <ToggleButton value="area" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Map Area</ToggleButton>
        <ToggleButton value="radius" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Radius</ToggleButton>
      </ToggleButtonGroup>

      {/* Language filter */}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={language}
        onChange={(_, v) => { if (v) setLanguage(v); }}
        sx={{ mb: 1 }}
        fullWidth
      >
        <ToggleButton value="all" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
          <LanguageIcon sx={{ fontSize: 14, mr: 0.5 }} /> All
        </ToggleButton>
        <ToggleButton value="en" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>English</ToggleButton>
        <ToggleButton value="foreign" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
          <PublicIcon sx={{ fontSize: 14, mr: 0.5 }} /> Foreign
        </ToggleButton>
      </ToggleButtonGroup>

      <TextField
        size="small"
        fullWidth
        label="Keywords (optional)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
        sx={{ mb: 1 }}
      />

      <FormControl size="small" fullWidth sx={{ mb: 1 }}>
        <InputLabel>Category</InputLabel>
        <Select
          value={category}
          label="Category"
          onChange={(e) => setCategory(e.target.value)}
        >
          <MenuItem value=""><em>Any</em></MenuItem>
          {categories.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {mode === 'radius' && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">Radius: {radiusKm} km</Typography>
          <Slider size="small" value={radiusKm} min={5} max={500} step={5} onChange={(_, v) => setRadiusKm(v as number)} />
        </Box>
      )}

      {!hasExplicitRange && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">Last {days} days</Typography>
          <Slider size="small" value={days} min={1} max={365} step={1} onChange={(_, v) => setDays(v as number)} />
        </Box>
      )}
      {hasExplicitRange && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Using tab date range: {dateRange.start?.toLocaleDateString()} – {dateRange.end?.toLocaleDateString()}
        </Typography>
      )}

      <Button
        variant="contained"
        size="small"
        onClick={handleSearch}
        disabled={searching}
        startIcon={searching ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
        sx={{ mb: 1, bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
      >
        {searching ? 'Searching…' : 'Search Media'}
      </Button>

      {error && (
        <Typography variant="caption" color={results.length ? 'text.secondary' : 'error'} sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {results.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
          <Chip label={`${results.length} articles`} size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#e65100', fontWeight: 'bold' }} />
          <Tooltip title="Plot every geocoded location from these articles on the map">
            <span>
              <Button
                size="small"
                startIcon={<MapIcon />}
                disabled={geolocated.length === 0}
                onClick={() => onLoadArticlesToMap?.(geolocated)}
                sx={{ fontSize: '0.7rem' }}
              >
                Load {totalPoints} point{totalPoints === 1 ? '' : 's'} to Map
              </Button>
            </span>
          </Tooltip>
          {geolocated.length > 0 && onZoomToExtent && (
            <Tooltip title="Zoom to all plotted locations">
              <Button
                size="small"
                startIcon={<ZoomOutMapIcon />}
                onClick={() => onZoomToExtent(
                  geolocated.flatMap((a) => (a.locations && a.locations.length > 0)
                    ? a.locations
                    : (a.latitude != null && a.longitude != null
                        ? [{ latitude: a.latitude, longitude: a.longitude }]
                        : [])),
                )}
                sx={{ fontSize: '0.7rem' }}
                color="inherit"
              >
                Zoom to Extent
              </Button>
            </Tooltip>
          )}
          {loadedArticles.length > 0 && (
            <Button size="small" startIcon={<ClearIcon />} onClick={onClearArticles} sx={{ fontSize: '0.7rem' }} color="inherit">
              Clear Map
            </Button>
          )}
        </Box>
      )}

      <Divider sx={{ mb: 1 }} />

      {/* Results list */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {results.map((a) => (
          <Box
            key={a.id}
            sx={{
              p: 1,
              mb: 0.75,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '&:hover': { borderColor: '#ff9800' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Box
                sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.75, flexShrink: 0,
                  bgcolor: SENTIMENT_COLOR[a.sentiment || 'neutral'] || '#9e9e9e' }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, cursor: 'pointer', lineHeight: 1.25 }}
                onClick={() => openDetail(a)}
              >
                {a.title}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">{formatDate(a.published_at)}</Typography>
              {a.is_translated && (
                <Chip label="Foreign" size="small" icon={<PublicIcon sx={{ fontSize: 12 }} />}
                  sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(63,81,181,0.12)', color: '#3f51b5' }} />
              )}
              {a.primary_category && (
                <Chip label={a.primary_category} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
              )}
              {a.location_name && (
                <Typography variant="caption" color="text.secondary">
                  · {a.location_name}{a.country ? `, ${a.country}` : ''}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.25, mt: 0.5, alignItems: 'center' }}>
              {(a.locations?.length || 0) > 1 && (
                <Chip
                  icon={<PlaceIcon sx={{ fontSize: 12 }} />}
                  label={a.locations!.length}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-icon': { ml: '4px' } }}
                />
              )}
              {a.latitude != null && a.longitude != null && (
                <Tooltip title="Zoom to primary location">
                  <IconButton size="small" onClick={() => onZoomTo?.(a.latitude as number, a.longitude as number)}>
                    <MyLocationIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
              {(a.locations?.length || 0) > 1 && onZoomToExtent && (
                <Tooltip title="Zoom to all locations">
                  <IconButton size="small" onClick={() => onZoomToExtent(a.locations as any)}>
                    <ZoomOutMapIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
              {a.url && (
                <Tooltip title="Open source">
                  <IconButton size="small" component="a" href={a.url} target="_blank" rel="noreferrer">
                    <OpenInNewIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
              {(intelEnabled || onSaveToIntel) && (
                <Tooltip title={savedIds.has(a.id) ? 'Saved to Intel Packet' : 'Save to Intel Packet'}>
                  <IconButton size="small" onClick={() => handleSave(a)} disabled={savedIds.has(a.id)}>
                    {savedIds.has(a.id)
                      ? <CheckCircleIcon sx={{ fontSize: 15, color: '#4caf50' }} />
                      : <BookmarkAddIcon sx={{ fontSize: 15 }} />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Detail dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{detail.title}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">{formatDate(detail.published_at)}</Typography>
                {detail.source && <Chip label={detail.source} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
                {detail.is_translated && <Chip label="Foreign / translated" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(63,81,181,0.12)', color: '#3f51b5' }} />}
                {detail.sentiment && <Chip label={detail.sentiment} size="small" sx={{ height: 18, fontSize: '0.6rem', color: SENTIMENT_COLOR[detail.sentiment] }} />}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {detailLoading && <LinearProgress sx={{ mb: 1 }} />}
              {(detail.full_text || detail.summary) && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>
                  {detail.full_text || detail.summary}
                </Typography>
              )}
              {(detail.locations?.length || 0) > 0 ? (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Locations ({detail.locations!.length})
                    </Typography>
                    {onZoomToExtent && detail.locations!.length > 1 && (
                      <Button size="small" startIcon={<ZoomOutMapIcon sx={{ fontSize: 14 }} />}
                        onClick={() => onZoomToExtent(detail.locations as any)} sx={{ fontSize: '0.65rem' }}>
                        Zoom to extent
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ mt: 0.25 }}>
                    {detail.locations!.map((l, i) => (
                      <Box key={`${l.latitude},${l.longitude},${i}`}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Zoom to this location">
                          <IconButton size="small" onClick={() => onZoomTo?.(l.latitude, l.longitude)}>
                            <MyLocationIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          {l.name || `${l.latitude.toFixed(3)}, ${l.longitude.toFixed(3)}`}
                          {l.country ? ` · ${l.country}` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (detail.location_name || detail.country) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Location: {detail.location_name}{detail.country ? `, ${detail.country}` : ''}
                  {detail.latitude != null && ` (${detail.latitude.toFixed(3)}, ${detail.longitude?.toFixed(3)})`}
                </Typography>
              )}
              {!!detail.themes?.length && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Themes</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                    {detail.themes.map((t) => <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />)}
                  </Box>
                </Box>
              )}
              {!!detail.persons?.length && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>People</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                    {detail.persons.map((p) => <Chip key={p} label={p} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />)}
                  </Box>
                </Box>
              )}
              {!!detail.organizations?.length && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Organizations</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                    {detail.organizations.map((o) => <Chip key={o} label={o} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />)}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {detail.url && (
                <Button size="small" startIcon={<OpenInNewIcon />} component="a" href={detail.url} target="_blank" rel="noreferrer">
                  Open Source
                </Button>
              )}
              {(intelEnabled || onSaveToIntel) && (
                <Button
                  size="small"
                  startIcon={savedIds.has(detail.id) ? <CheckCircleIcon /> : <BookmarkAddIcon />}
                  disabled={savedIds.has(detail.id)}
                  onClick={() => handleSave(detail)}
                >
                  {savedIds.has(detail.id) ? 'Saved' : 'Save to Intel Packet'}
                </Button>
              )}
              <Button size="small" onClick={() => setDetail(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <PromoteToPacketDialog
        open={!!promoteArticle}
        onClose={() => setPromoteArticle(null)}
        target={promoteArticle ? {
          member_type: 'overwatch_article',
          member_id: promoteArticle.id,
          label: promoteArticle.title,
          is_primary: true,
          meta: {
            url: promoteArticle.url,
            published_at: promoteArticle.published_at,
            latitude: promoteArticle.latitude,
            longitude: promoteArticle.longitude,
            osint: {
              article_id: promoteArticle.id,
              title: promoteArticle.title,
              headline: promoteArticle.headline,
              url: promoteArticle.url,
              source: promoteArticle.source,
              language: promoteArticle.language,
              is_translated: promoteArticle.is_translated,
              published_at: promoteArticle.published_at,
              primary_category: promoteArticle.primary_category,
              sentiment: promoteArticle.sentiment,
              sentiment_score: promoteArticle.sentiment_score,
              summary: promoteArticle.summary,
              body: promoteArticle.full_text || promoteArticle.summary,
              place: promoteArticle.location_name,
              latitude: promoteArticle.latitude,
              longitude: promoteArticle.longitude,
              country: promoteArticle.country,
              locations: promoteArticle.locations || [],
              themes: promoteArticle.themes || [],
              persons: promoteArticle.persons || [],
              organizations: promoteArticle.organizations || [],
            },
          },
        } : null}
        defaultName={promoteArticle ? `News: ${promoteArticle.title}` : undefined}
        evidence={promoteArticle ? {
          title: promoteArticle.title || 'OSINT article',
          body: [
            promoteArticle.summary || promoteArticle.title || '',
            promoteArticle.url ? `Source: ${promoteArticle.url}` : '',
          ].filter(Boolean).join('\n\n'),
          kind: 'article',
          source: 'overwatch',
        } : undefined}
        onPromoted={() => {
          if (promoteArticle) setSavedIds((prev) => new Set(prev).add(promoteArticle.id));
        }}
      />
    </Box>
  );
};

export default OverwatchPanel;
